import React from "react";
import {
  Box,
  Select,
  MenuItem,
  Chip,
  FormControl,
  Typography,
  Button,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import styles from "./document-toolbar.module.scss";

interface DocumentToolbarProps {
  sortBy?: string;
  onSortChange?: (value: string) => void;
  filterBy?: string;
  onFilterChange?: (value: string) => void;
  riskTierFilter?: string;
  onRiskTierFilterChange?: (value: string) => void;
  onResetFilters?: () => void;
  showAddBack?: boolean;
  onAddBack?: (event: React.MouseEvent<HTMLElement>) => void;
  instructionText?: string;
}

export default function DocumentToolbar({
  sortBy = "document-order",
  onSortChange,
  filterBy = "all",
  onFilterChange,
  riskTierFilter = "all",
  onRiskTierFilterChange,
  onResetFilters,
  showAddBack = false,
  onAddBack,
  instructionText = "Each document has its own card below. Check the source and mark each finding Verified or Not Verified, then finalize.",
}: DocumentToolbarProps): JSX.Element {
  const hasActiveFilters = filterBy !== "all" || riskTierFilter !== "all";
  return (
    <Box className={styles.toolbar}>
      <Typography className={styles.instructionText}>
        {instructionText}
      </Typography>

      <Box className={styles.controls}>
        <Box className={styles.controlGroup}>
          {/* <Chip label="NEW" size="small" className={styles.newBadge} /> */}

          <Box className={styles.sortControl}>
            <Typography className={styles.controlLabel}>Sort by:</Typography>
            <FormControl size="small" className={styles.select}>
              <Select
                value={sortBy}
                onChange={(e) => onSortChange?.(e.target.value)}
                sx={{
                  fontSize: "13px",
                  "& .MuiSelect-select": { padding: "6px 32px 6px 12px" },
                }}
              >
                <MenuItem value="document-order">Document order</MenuItem>
                <MenuItem value="risk-high">Risk (High to Low)</MenuItem>
                <MenuItem value="risk-low">Risk (Low to High)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {onFilterChange && (
            <Box className={styles.filterControl}>
              <Typography className={styles.controlLabel}>
                Verification:
              </Typography>
              <FormControl size="small" className={styles.select}>
                <Select
                  value={filterBy}
                  onChange={(e) => onFilterChange?.(e.target.value)}
                  sx={{
                    fontSize: "13px",
                    "& .MuiSelect-select": { padding: "6px 32px 6px 12px" },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="verified">Verified</MenuItem>
                  <MenuItem value="not-verified">Not Verified</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {onRiskTierFilterChange && (
            <Box className={styles.filterControl}>
              <Typography className={styles.controlLabel}>
                Risk Tier:
              </Typography>
              <FormControl size="small" className={styles.select}>
                <Select
                  value={riskTierFilter}
                  onChange={(e) => onRiskTierFilterChange?.(e.target.value)}
                  sx={{
                    fontSize: "13px",
                    "& .MuiSelect-select": { padding: "6px 32px 6px 12px" },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {hasActiveFilters && onResetFilters && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={onResetFilters}
              sx={{
                fontSize: "13px",
                textTransform: "none",
                borderColor: "#d32f2f",
                color: "#d32f2f",
                "&:hover": {
                  borderColor: "#c62828",
                  backgroundColor: "rgba(211, 47, 47, 0.04)",
                },
              }}
            >
              Reset Filters
            </Button>
          )}
        </Box>

        {showAddBack && onAddBack && (
          <Box
            className={styles.addBackControl}
            onClick={(e) => onAddBack(e as React.MouseEvent<HTMLElement>)}
          >
            <Typography className={styles.addBackText}>
              + Add document back...
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
